// ─── Product Conflict View ───────────────────────────────────────────────────
//
// Sky/blue accent to match ENTITY_TYPE_CONFIG.PRODUCT.tone = 'tenantApproved'.
// Traceability: FR-SYNC-013 (conflict resolution).

import { PRODUCT_SCHEMA } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { GroupCard } from './GroupCard';
import { ConflictHeader } from './ConflictHeader';

export interface ProductConflictViewProps {
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
  deviceId: string | null;
  conflictMessage?: string;
  /**
   * Tenant currency for the Pricing fields (price/cost). Defaults to `'INR'`
   * (byte-identical); non-INR threads the correct symbol/subunit/decimals.
   */
  currency?: CurrencyCode;
}

export function ProductConflictView({
  clientVersion,
  serverVersion,
  deviceId,
  conflictMessage,
  currency = 'INR',
}: ProductConflictViewProps) {
  const productName =
    (clientVersion.name as string | undefined) ??
    (serverVersion.name as string | undefined) ??
    'Unknown';

  const sku =
    (clientVersion.sku as string | undefined) ??
    (serverVersion.sku as string | undefined);

  const isActive =
    (clientVersion.is_active as boolean | undefined) ??
    (serverVersion.is_active as boolean | undefined);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="product-conflict-view">
      <ConflictHeader
        entityLabel="Product Conflict"
        identifier={productName}
        accent={{
          from: 'from-sky-500/10',
          to: 'to-blue-500/10',
          ring: 'ring-sky-500/20',
          text: 'text-sky-700',
          darkText: 'dark:text-sky-300',
        }}
        tone="info"
        badgeVariant="tenantApproved"
        badgeLabel={isActive === false ? 'Inactive' : 'Active'}
        subtitle={sku ? (
          <p className="mt-0.5 text-xs font-mono text-neutral-500 dark:text-neutral-400">
            {sku}
          </p>
        ) : undefined}
        deviceId={deviceId}
        conflictMessage={conflictMessage}
        ariaLabel="Product conflict header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {PRODUCT_SCHEMA.groups.map((group, i) => (
          <GroupCard
            key={group.title}
            title={group.title}
            fields={group.fields}
            clientVersion={clientVersion}
            serverVersion={serverVersion}
            currency={currency}
            delay={i * 60}
          />
        ))}
      </div>
    </div>
  );
}
